/*
 * Simple chat interface for our agent.
 */
import { useEffect, useState } from 'react';

import { QuestionInput } from '@/features/Minds/QuestionInput';
import { UserQueryWidget } from '@/features/Minds/UserQueryWidget';
import { HumanInputRequestWidget } from '@/features/maintenance/HumanInputRequestWidget';
import { WorkflowSteps } from '@/features/maintenance/WorkflowSteps';
import AgentLanding from '@/features/maintenance/agent-landing';
import ErrorWidget from '@/features/maintenance/error-widget';
import {
    AgentOutput,
    ChatMessage,
    ErrorMessage,
    HumanInputRequest,
    ToolOutput,
    ToolStart,
    UserQuery,
    useStatelessChat,
} from '@/features/maintenance/hooks/useStatelessChat';
import MemoryWidget from '@/features/maintenance/memory-widget';
import OutputWidget from '@/features/maintenance/output-widget';
import Sidebar from '@/features/maintenance/sidebar-layout';
import ToolWidget from '@/features/maintenance/tool-widget';
import { SidebarData } from '@/features/maintenance/types';
import { createFileRoute } from '@tanstack/react-router';

import { useAutoScroll } from '@/hooks/useAutoScroll';
import { useSettings } from '@/hooks/useSettings';

import { useLLMModels } from '@/lib/api';
import { LLMModel } from '@/lib/types';

export const Route = createFileRoute('/maintenance/agent')({
    component: MaintenanceAgent,
});

// Tool type to store a full tool call
// this includes:
// - name
// - output (assume each tool has only one output)
// - progress (not supported yet)
export type Tool = {
    type: 'Tool';
    name: string;
    output: string | undefined;
};

export type Thinking = {
    type: 'Thinking';
};

type ProcessedMessage =
    | UserQuery
    | AgentOutput
    | Tool
    | Thinking
    | HumanInputRequest
    | ErrorMessage;

function MaintenanceAgent() {
    const { settings } = useSettings();
    const { data: llmModels } = useLLMModels();
    const [aiModel, setAIModel] = useState<LLMModel | undefined>(undefined);
    const {
        progress,
        messages,
        sendMessage,
        isRunning,
        latestMemory,
        cancel,
        reset,
        resetMemory,
        skipHumanInput,
        agentState,
        isDeepResearchMode,
    } = useStatelessChat();
    const scrollRef = useAutoScroll(messages);
    const [processedMessages, setProcessedMessages] = useState<
        ProcessedMessage[]
    >([]);
    const [isThinking, setIsThinking] = useState(false);

    useEffect(() => {
        if (!aiModel && llmModels && llmModels.length > 0) {
            setAIModel(llmModels[0]);
        }
    }, [llmModels, aiModel]);

    const handleSend = (query: string) => {
        sendMessage({ query: query, model: aiModel });
    };

    const processMessage = (message: ChatMessage) => {
        switch (message.type) {
            case 'Thinking': {
                setIsThinking(true);
                break;
            }
            case 'ToolStart': {
                setIsThinking(false);
                const toolName = (message as ToolStart).value.name;
                const tool: Tool = {
                    type: 'Tool',
                    name: toolName,
                    output: undefined,
                };
                setProcessedMessages((prev) => [...prev, tool]);
                break;
            }
            case 'ToolOutput': {
                const toolName = (message as ToolOutput).value.name;
                const toolOutput = (message as ToolOutput).value.output;
                const tool = processedMessages.find(
                    (msg) => msg.type === 'Tool' && msg.name === toolName,
                ) as Tool | undefined;
                if (!tool) return;
                setProcessedMessages((prev) =>
                    prev.map((msg) =>
                        msg.type === 'Tool' && msg.name === toolName
                            ? { ...msg, output: toolOutput }
                            : msg,
                    ),
                );
                break;
            }
            case 'UserQuery': {
                setProcessedMessages((prev) => [...prev, message as UserQuery]);
                break;
            }
            case 'AgentOutput': {
                setIsThinking(false);
                setProcessedMessages((prev) => [
                    ...prev,
                    message as AgentOutput,
                ]);
                break;
            }
            case 'HumanInputRequest': {
                setIsThinking(false);
                setProcessedMessages((prev) => [
                    ...prev,
                    message as HumanInputRequest,
                ]);

                break;
            }
            case 'Error': {
                setIsThinking(false);
                setProcessedMessages((prev) => [
                    ...prev,
                    message as ErrorMessage,
                ]);
                break;
            }
        }
    };

    const [sidebarData, setSidebarData] = useState<SidebarData | null>(null);

    useEffect(() => {
        const latestMessage = messages.at(-1);
        if (!latestMessage) return;
        processMessage(latestMessage);
        if (latestMessage.type === 'AgentOutput') {
            const { content, quotes } = latestMessage.value;
            const tickets = quotes.filter((q) => q.ticket);
            const damageReports = quotes.filter((q) => q.dr);
            const engineeringOrders = quotes.filter((q) => q.eo);
            const tables = quotes.filter(
                (q) => q.tablePl && q.tablePl.data && q.tablePl.data.length > 0,
            );
            const stringQuotes = quotes.filter((q) => q.quote && q.quote != '');
            setSidebarData({
                tickets: tickets,
                damageReports: damageReports,
                engineeringOrders: engineeringOrders,
                tables: tables,
                quotes: stringQuotes,
            });
        }
    }, [messages]);

    // Function to transfer data from output widget to sidebar
    const handleDataTransfer = (data: SidebarData) => {
        setSidebarData(data);
    };

    const resetSidebar = () => {
        setSidebarData(null);
        setProcessedMessages([]);
        setIsThinking(false);
    };

    const [inputValue, setInputValue] = useState('');

    const handleTotalReset = () => {
        resetMemory(resetSidebar);
    };

    return (
        <div className="flex h-screen" data-testid="MaintenanceAgent">
            <div className="flex-1 overflow-hidden h-full relative lg:mx-0 mx-4 bg-white">
                {/* Content */}
                <div
                    ref={scrollRef}
                    className="flex flex-col pt-8 items-center h-full overflow-auto sm:px-4 sm:pt-4 pb-[10em]"
                >
                    <div className="max-w-[800px] flex flex-col gap-3 w-full">
                        {messages.length === 0 ? (
                            <AgentLanding
                                inputValue={inputValue}
                                setInputValue={setInputValue}
                                handleSend={handleSend}
                                isRunning={isRunning}
                                cancel={cancel}
                                resetMemory={handleTotalReset}
                                llmModels={llmModels || []}
                                aiModel={aiModel}
                                setAIModel={setAIModel}
                                showAiSettings={false}
                            />
                        ) : (
                            processedMessages.map((msg, index) => (
                                <div key={index}>
                                    {msg.type === 'UserQuery' && (
                                        <UserQueryWidget
                                            userQuery={
                                                (msg as UserQuery).value.query
                                            }
                                        />
                                    )}
                                    {msg.type === 'Tool' && (
                                        <ToolWidget
                                            tool={msg as Tool}
                                            isLatestTool={
                                                index ===
                                                processedMessages.length - 1
                                            }
                                            isRunning={isRunning}
                                        />
                                    )}
                                    {msg.type === 'AgentOutput' && (
                                        <OutputWidget
                                            output={msg as AgentOutput}
                                            onTransferData={handleDataTransfer}
                                        />
                                    )}
                                    {msg.type === 'HumanInputRequest' && (
                                        <HumanInputRequestWidget
                                            request={msg as HumanInputRequest}
                                            onAnswerSelected={setInputValue}
                                            onSkip={skipHumanInput}
                                        />
                                    )}
                                    {msg.type === 'Error' && (
                                        <ErrorWidget
                                            error={msg as ErrorMessage}
                                        />
                                    )}
                                </div>
                            ))
                        )}
                        {agentState && (
                            <WorkflowSteps
                                agentState={agentState}
                                isDeepResearchMode={isDeepResearchMode}
                            />
                        )}
                    </div>
                </div>

                {messages.length > 0 && (
                    /* Search Bar */
                    <div className="absolute bottom-0 w-full flex justify-center pb-3 sm:p-4">
                        <div className="w-full max-w-3xl">
                            <QuestionInput
                                inputValue={inputValue}
                                setInputValue={setInputValue}
                                onSubmit={handleSend}
                                popOver={
                                    settings.isDebug &&
                                    latestMemory && (
                                        <MemoryWidget memory={latestMemory} />
                                    )
                                }
                                isRunning={isRunning}
                                onCancel={cancel}
                                showNew={messages.length > 0}
                                onNew={handleTotalReset}
                            />
                        </div>
                    </div>
                )}
            </div>
            {/* Sidebar for displaying related content */}
            {sidebarData &&
                (sidebarData.tickets.length > 0 ||
                    sidebarData.damageReports.length > 0 ||
                    sidebarData.engineeringOrders.length > 0 ||
                    sidebarData.tables.length > 0) &&(
                    <Sidebar
                        data={sidebarData}
                    />
                )}
        </div>
    );
}
